import React, { useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ApButtonText,
  ApLoaderDefault,
  ApLoaderText,
  ApSimpleDropdown,
} from '@alixpartners/ui-components';
import {
  Wrapper,
  TableWrapper,
  ShowMoreWrapper,
  LoaderWrapper,
  NoData,
  Title,
  TitleWrapper,
  Subtitle,
} from './BenchmarksTable.styles';
import { ICurrencyFormat } from 'utils/types';
import { getColumnDefsByType } from './utils';
import { CompareValueEnum } from '../utils';
import {
  EXPORT_OPTIONS,
  HEADER_CURRENCIES_TEXT,
  UNITS_SIGNS,
} from 'utils/constants';

interface IProps {
  data?: any[];
  format: ICurrencyFormat;
  handleExport: (b?: boolean) => void;
  isLoading?: boolean;
  pageSize?: number;
  isPercentage?: boolean;
  title: string;
  type: string;
}

const HEADER_HEIGHT = 30;
const ROW_HEIGHT = 24;
const DEFAULT_PAGE_SIZE = 10;

const getAggregatedValue = (data: any[], key: string) => {
  return data.reduce((acc, item) => acc + item[key], 0);
};

export const BenchmarksTable: React.FC<IProps> = ({
  data,
  format,
  handleExport,
  isLoading,
  isPercentage,
  pageSize = DEFAULT_PAGE_SIZE,
  title,
  type,
}) => {
  const [gridApi, setGridApi] = React.useState<any>(null);
  const [extraRows, setExtraRows] = React.useState(0);
  const paginationPageSize = pageSize * (extraRows + 1);
  const displayedData = data ? data.slice(0, paginationPageSize) : [];

  useEffect(() => {
    setExtraRows(0);

    if (gridApi) {
      gridApi.paginationSetPageSize(pageSize);
    }
  }, [data, gridApi, pageSize]);

  const getTotalRow = () => {
    const isHeadcount = type === CompareValueEnum.HEADCOUNT;

    if (!isHeadcount || !data) {
      return undefined;
    }

    return [
      {
        category: 'TOTAL',
        clientCategory: '',
        spend: getAggregatedValue(data, 'spend'),
        value: getAggregatedValue(data, 'value'),
        avarageSpend: getAggregatedValue(data, 'avarageSpend'),
      },
    ];
  };

  const subtitle = `${HEADER_CURRENCIES_TEXT[format.currency]}, ${
    UNITS_SIGNS[format.unit]
  }`;
  const gridOptions = {
    defaultColDef: {
      suppressMenu: true,
    },
    rowData: data,
    rowHeight: ROW_HEIGHT,
    headerHeight: HEADER_HEIGHT,
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    params.api.sizeColumnsToFit();
    params.api.paginationSetPageSize(pageSize);
  };

  return (
    <Wrapper>
      <TitleWrapper>
        <Title>
          {title}
          <Subtitle>{subtitle}</Subtitle>
        </Title>

        <ApSimpleDropdown
          iconName="outline_save_alt"
          onChange={() => {
            handleExport(true);
          }}
          options={[EXPORT_OPTIONS[1]]}
          label="Export"
        />
      </TitleWrapper>
      {!data || isLoading ? (
        <LoaderWrapper>
          <ApLoaderDefault loaderText={() => <ApLoaderText />} />
        </LoaderWrapper>
      ) : data && data.length === 0 ? (
        <NoData>No data available</NoData>
      ) : (
        <React.Fragment>
          <TableWrapper>
            <div className="ag-theme-balham">
              <AgGridReact
                {...gridOptions}
                columnDefs={getColumnDefsByType(type, format, isPercentage)}
                onGridReady={onGridReady}
                pinnedTopRowData={getTotalRow()}
                suppressCopyRowsToClipboard
                suppressScrollOnNewData
                suppressRowHoverHighlight
                suppressRowDrag
                suppressRowClickSelection
                suppressCellSelection
                suppressCsvExport
                suppressExcelExport
                suppressContextMenu
                domLayout="autoHeight"
                paginationPageSize={paginationPageSize}
                pagination
                suppressPaginationPanel
              />
            </div>
          </TableWrapper>
          <ShowMoreWrapper>
            {displayedData.length}/{data.length}
            {displayedData.length !== data.length && (
              <ApButtonText
                onClick={() => {
                  if (gridApi) {
                    gridApi.paginationSetPageSize(
                      paginationPageSize + pageSize
                    );
                  }

                  setExtraRows(extraRows + 1);
                }}
              >
                SHOW MORE...
              </ApButtonText>
            )}
          </ShowMoreWrapper>
        </React.Fragment>
      )}
    </Wrapper>
  );
};
